'use client';

import { useTranslations } from 'next-intl';
import { ClipboardCheck, Eye, RotateCcw } from 'lucide-react';
import { Link } from '@/i18n/routing';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { OcrQaListStatusBadge } from './ocr-qa-list-status-badge';
import type { OcrQaContainerListItem, OcrQaTier } from '../types';

const TIER_DETAIL_ROUTE: Record<OcrQaTier, string> = {
  1: '/dashboard/qa-ocr-1',
  2: '/dashboard/qa-ocr-2',
  3: '/dashboard/qa-ocr-3'
};

interface OcrQaContainerCardProps {
  item: OcrQaContainerListItem;
  tier: OcrQaTier;
}

export function OcrQaContainerCard({ item, tier }: OcrQaContainerCardProps) {
  const t = useTranslations('ocrQa');

  const detailHref = `${TIER_DETAIL_ROUTE[tier]}/${item.container_id}`;
  const oldestPendingDisplay = item.oldest_pending_at
    ? new Date(item.oldest_pending_at).toLocaleString()
    : '—';

  return (
    <Card className='border-border hover:border-primary/30 transition-colors'>
      <CardContent className='p-6'>
        <div className='mb-4 flex items-start justify-between gap-3'>
          <div className='flex min-w-0 items-center gap-3'>
            <div className='bg-primary/10 flex h-9 w-9 shrink-0 items-center justify-center rounded-md'>
              <ClipboardCheck className='text-primary h-4 w-4' />
            </div>
            <div className='min-w-0'>
              <Link
                href={detailHref}
                className='hover:text-primary truncate font-medium hover:underline'
              >
                {item.container_number}
              </Link>
            </div>
          </div>
          <Badge variant='outline' className='shrink-0 text-xs'>
            {item.total_pages}
          </Badge>
        </div>

        <div className='mb-4 flex flex-wrap items-center gap-2'>
          <OcrQaListStatusBadge status={item.list_status} />
          {item.qa_return_count > 0 && (
            <Badge
              variant='outline'
              className='gap-1 text-[10px]'
              title={t('returnCount', { count: item.qa_return_count })}
            >
              <RotateCcw className='h-3 w-3' />
              {t('returnCount', { count: item.qa_return_count })}
            </Badge>
          )}
        </div>

        <div className='mb-4 space-y-2 text-sm'>
          <div className='flex items-center justify-between gap-2'>
            <span className='text-muted-foreground'>
              {t('columnPagesPending')}
            </span>
            <span className='font-medium tabular-nums'>
              {item.pending_count}
            </span>
          </div>
          <div className='flex items-center justify-between gap-2'>
            <span className='text-muted-foreground'>
              {t('columnPagesInProgress')}
            </span>
            <span className='font-medium tabular-nums'>
              {item.in_progress_count}
            </span>
          </div>
          <div className='flex items-center justify-between gap-2'>
            <span className='text-muted-foreground'>
              {t('columnOldestPending')}
            </span>
            <span className='text-muted-foreground truncate text-xs'>
              {oldestPendingDisplay}
            </span>
          </div>
        </div>

        <div className='flex items-center justify-end gap-1 border-t pt-3'>
          <Button asChild variant='outline' size='sm' className='h-8'>
            <Link href={detailHref}>
              <Eye className='me-1.5 h-3.5 w-3.5' />
              {t('open')}
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
